import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate, useBlocker } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import AdvanceIssueDialog from "@/components/navbar/AdvanceIssueDialog";
import { getCurrentIssue } from "@/lib/data/issue/currentIssue";
import { alertCategories, useEmailAlerts } from "@/hooks/useEmailAlerts";
import { EmailAlertSettings } from "@/components/EmailAlertSettings";

const Profile = () => {
  const { user, profile, updateProfile, profileLoading } = useAuth();
  const { preferences, loading: preferencesLoading, updatePreferences } = useUserPreferences();
  const { alerts, loading: alertsLoading, error: alertsError, saveAlerts } = useEmailAlerts();
  const [localAlerts, setLocalAlerts] = useState(alerts);
  const alertsChanged = alertCategories.some(([key]) => localAlerts[key] !== alerts[key]) ||
    (localAlerts.email ?? "").trim() !== (alerts.email ?? user?.email ?? "");
  const isAdmin = useIsAdmin();
  const [currentIssue, setCurrentIssue] = useState("");
  const [displayName, setDisplayName] = useState(profile?.display_name || "");
  const [username, setUsername] = useState(profile?.username || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Local state for preferences (only saved on "Save Changes")
  // UI uses "Hide 'list' articles"; stored preference remains show_list_articles.
  const [localAutoMarkRead, setLocalAutoMarkRead] = useState(preferences.auto_mark_read);
  const [localHideListArticles, setLocalHideListArticles] = useState(!preferences.show_list_articles);
  
  const navigate = useNavigate();
  const bypassBlockerRef = useRef(false);

  const hasUnsavedChanges = useMemo(() => {
    return (
      displayName !== (profile?.display_name || "") ||
      username !== (profile?.username || "") ||
      localAutoMarkRead !== preferences.auto_mark_read ||
      localHideListArticles !== !preferences.show_list_articles ||
      alertsChanged ||
      newPassword !== "" ||
      confirmPassword !== ""
    );
  }, [
    displayName,
    username,
    profile?.display_name,
    profile?.username,
    localAutoMarkRead,
    localHideListArticles,
    preferences.auto_mark_read,
    preferences.show_list_articles,
    newPassword,
    confirmPassword,
    alertsChanged,
  ]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !bypassBlockerRef.current &&
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // Warn on tab close / refresh when there are unsaved edits
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load current issue for admin dialog
  useEffect(() => {
    getCurrentIssue().then(({ text }) => setCurrentIssue(text));
  }, []);

  // Sync local state when data loads
  useEffect(() => {
    setLocalAlerts({ ...alerts, email: alerts.email ?? user?.email ?? "" });
  }, [alerts, user?.email]);

  useEffect(() => {
    setLocalAutoMarkRead(preferences.auto_mark_read);
    setLocalHideListArticles(!preferences.show_list_articles);
  }, [preferences]);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || "");
      setUsername(profile.username || "");
    }
  }, [profile]);

  const leaveWithoutSaving = useCallback(() => {
    bypassBlockerRef.current = true;
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      navigate("/");
    }
  }, [blocker, navigate]);

  const handleCancel = () => {
    navigate("/");
  };

  useEffect(() => {
    if (user && !profile && !profileLoading) {
      setIsCreatingProfile(true);
      createProfileForUser().catch(console.error);
    }
  }, [user, profile, profileLoading]);

  const createProfileForUser = async () => {
    if (!user) return;
    
    try {
      const { data, error: checkError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      
      if (checkError && checkError.code !== "PGRST116") {
        console.error("Error checking profile:", checkError);
        toast.error("Error checking profile");
        return;
      }
      
      if (data) {
        return;
      }
      
      const { error } = await supabase
        .from("profiles")
        .insert({
          id: user.id,
          username: null,
          display_name: null,
          avatar_url: null
        });
      
      if (error) {
        console.error("Error creating profile:", error);
        toast.error("Error creating profile");
        return;
      }
      
      toast.success("Profile created successfully");
      window.location.reload();
    } catch (error) {
      console.error("Error in createProfileForUser:", error);
    } finally {
      setIsCreatingProfile(false);
    }
  };

  const saveChanges = async (): Promise<boolean> => {
    setIsSubmitting(true);
    
    try {
      let changesMade = false;

      if (alertsError) throw new Error(alertsError);
      if (alertsChanged) {
        const email = (localAlerts.email ?? "").trim();
        const enabled = alertCategories.some(([key]) => localAlerts[key]);
        if ((enabled || email) && (!email || email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
          toast.error("Please enter a valid email address for alerts.");
          document.getElementById("alert-email")?.focus();
          return false;
        }
      }

      const profileChanged = 
        displayName !== (profile?.display_name || "") ||
        username !== (profile?.username || "");
      
      if (profileChanged) {
        const result = await updateProfile({
          display_name: displayName,
          username: username 
        });
        if (result.error) return false;
        changesMade = true;
      }

      const preferencesChanged = 
        localAutoMarkRead !== preferences.auto_mark_read ||
        localHideListArticles !== !preferences.show_list_articles;
      
      if (preferencesChanged) {
        const saved = await updatePreferences({
          auto_mark_read: localAutoMarkRead,
          show_list_articles: !localHideListArticles,
        });
        if (!saved) return false;
        changesMade = true;
      }

      if (alertsChanged) {
        const email = (localAlerts.email ?? "").trim();
        await saveAlerts({ ...localAlerts, email: email === user?.email || !email ? null : email });
        changesMade = true;
      }

      if (newPassword || confirmPassword) {
        if (newPassword !== confirmPassword) {
          toast.error("Passwords do not match");
          return false;
        }
        
        if (newPassword.length < 6) {
          toast.error("Password must be at least 6 characters long");
          return false;
        }
        
        const { error } = await supabase.auth.updateUser({
          password: newPassword
        });
        
        if (error) {
          toast.error("Failed to update password: " + error.message);
          return false;
        }
        
        setNewPassword("");
        setConfirmPassword("");
        changesMade = true;
      }

      if (changesMade) {
        toast.success("Changes saved successfully");
      } else {
        toast.info("No changes to save");
      }
      return true;
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAll = async () => {
    const saved = await saveChanges();
    if (!saved) return;
    bypassBlockerRef.current = true;
    navigate("/");
  };

  const handleSaveAndLeave = async () => {
    const saved = await saveChanges();
    if (!saved) return;
    bypassBlockerRef.current = true;
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      navigate("/");
    }
  };

  const handleStay = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  if (profileLoading || isCreatingProfile || preferencesLoading || alertsLoading) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-[70vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="ml-2">{isCreatingProfile ? "Creating your profile..." : "Loading..."}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container max-w-3xl py-10">
        <h1 className="text-3xl font-bold mb-6">Profile Settings</h1>
        
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Update your profile information</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                value={user?.email || ""} 
                disabled 
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email cannot be changed
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input 
                id="username" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
              />
              <p className="text-xs text-muted-foreground">
                This is your unique username
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name (or Initials)</Label>
              <Input 
                id="displayName" 
                value={displayName} 
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your preferred display name or initials"
              />
              <p className="text-xs text-muted-foreground">
                This is the name or initials that will be displayed to other users
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Reading Preferences</CardTitle>
            <CardDescription>Manage how articles are marked as read</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="auto-mark-read"
                  checked={localAutoMarkRead}
                  onCheckedChange={(checked) => {
                    if (typeof checked === 'boolean') {
                      setLocalAutoMarkRead(checked);
                    }
                  }}
                />
                <Label htmlFor="auto-mark-read" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Automatically mark opened articles as read
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, articles will be automatically marked as read when you open them. You can still manually mark articles using the checkbox on each article card.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hide-list-articles"
                  checked={localHideListArticles}
                  onCheckedChange={(checked) => {
                    if (typeof checked === 'boolean') {
                      setLocalHideListArticles(checked);
                    }
                  }}
                />
                <Label htmlFor="hide-list-articles" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Hide 'list' articles
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, articles with the 'list' keyword are hidden from each issue. These are recurring reference articles that appear across all monthly issues.
              </p>
            </div>
          </CardContent>
        </Card>

        <EmailAlertSettings value={localAlerts} onChange={setLocalAlerts} error={alertsError} />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input 
                id="newPassword" 
                type="password"
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={6}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input 
                id="confirmPassword" 
                type="password"
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                minLength={6}
              />
            </div>
          </CardContent>
        </Card>

        {isAdmin && currentIssue && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Admin Settings</CardTitle>
              <CardDescription>Manage site-wide settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Current Issue: <strong>{currentIssue}</strong></p>
                  <p className="text-xs text-muted-foreground mt-1">Advance to the next monthly issue when you're ready</p>
                </div>
                <AdvanceIssueDialog currentIssue={currentIssue} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom action buttons */}
        <div className="mt-6 flex justify-between">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAll}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </div>

      <AlertDialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open) handleStay();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes on this page. Do you want to save them before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <AlertDialogCancel onClick={handleStay} disabled={isSubmitting}>
              Keep editing
            </AlertDialogCancel>
            <Button
              type="button"
              variant="outline"
              onClick={leaveWithoutSaving}
              disabled={isSubmitting}
            >
              Don't save
            </Button>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleSaveAndLeave();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Profile;
